import { Component, DestroyRef, inject, signal,  OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CheckoutMode, InvoicingService, IPaymentMethod } from '../../../core/services/invoicing.service';
import { ToolsService } from '../../../core/services/tools.service';
import { BsModalService } from 'ngx-bootstrap/modal';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { StoresService } from '../../../core/services/stores.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SaleDetailsComponent } from '../sale-details/sale-details.component';
import { ApiGatewayService } from '../../../core/services/api-gateway.service';
import { AlertComponent } from '../../../shared-with-client/alert/alert.component';
import { LoaderComponent } from '../../../shared-with-client/loader/loader.component';
import { MerchantCurrencyPipe } from '../../../shared/pipes/merchant-currency.pipe';
import { ButtonComponent } from '../../../shared-with-client/button/button.component';
import { ITeyaPaymentStatus, TeyaService } from '../../../core/services/teya.service';
import { ToastNotificationsService } from '../../../core/services/toast-notifications.service';
import { Subject, switchMap, takeUntil, takeWhile, timer } from 'rxjs';
import { BsDropdownDirective, BsDropdownMenuDirective, BsDropdownToggleDirective } from 'ngx-bootstrap/dropdown';

const POLL_INTERVAL_MS = 1500;
const MAX_POLL_MS = 10 * 60 * 1000;
const FINAL_PAYMENT_STATUSES = ['SUCCESSFUL', 'FAILED', 'CANCELLED'];
const SUCCESSFUL_PAYMENT_STATUS = 'SUCCESSFUL';
@Component({
    selector: 'app-invoice-success',
    imports: [
        TranslatePipe,
        AlertComponent,
        BsDropdownDirective,
        BsDropdownToggleDirective,
        BsDropdownMenuDirective,
        LoaderComponent, MerchantCurrencyPipe, ButtonComponent
    ],
    templateUrl: './invoice-success.component.html',
    styleUrl: './invoice-success.component.scss',
    encapsulation: ViewEncapsulation.None,
})
export class InvoiceSuccessComponent implements OnInit {
    private invoicingService = inject(InvoicingService);
    private toolsService = inject(ToolsService);
    private router = inject(Router);
    private bsModalService = inject(BsModalService);
    private storesService = inject(StoresService);
    private api = inject(ApiGatewayService);
    private teyaService = inject(TeyaService);
    private toastNotificationsService = inject(ToastNotificationsService);
    private translateService = inject(TranslateService);
    private destroyRef = inject(DestroyRef);
    private activatedRoute = inject(ActivatedRoute);

    public currentStore = toSignal(this.storesService.getActiveStore());
    public completeInvoiceData = toSignal(this.invoicingService.completeInvoiceData$);
    public unchargedTeyaAmount = toSignal(this.invoicingService.unchargedTeyaAmount$);

    public checkoutMode = toSignal(this.invoicingService.checkoutMode$, { initialValue: null });
    public CheckoutMode = CheckoutMode;

    public callTeyaTerminal = signal(!!this.activatedRoute.snapshot.data.teyaTerminal);
    public waitingForTeyaTerminal = signal(this.callTeyaTerminal());
    public teyaPaymentFailed = signal(false);
    public cancellingTeyaPayment = signal(false);

    private stopPolling$ = new Subject<void>();
    private activeTeyaPaymentRequestId: string | null = null;

    ngOnInit(): void {
        if (!this.completeInvoiceData()?.id) {
            this.goToStart();
            return;
        }

        if (this.callTeyaTerminal()) {
            this.sendSaleToTeyaTerminal();
        }
    }

    sendSaleToTeyaTerminal() {
        this.waitingForTeyaTerminal.set(true);
        this.teyaPaymentFailed.set(false);
        this.activeTeyaPaymentRequestId = null;

        this.teyaService.initiateSalePayment(this.completeInvoiceData()?.id, this.unchargedTeyaAmount()).subscribe(
            (result) => {
                this.activeTeyaPaymentRequestId = result.paymentRequestId;
                this.pollPaymentStatus(this.completeInvoiceData().id, result.paymentRequestId);
            },
            (error) => {
                this.toastNotificationsService.showToast(error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'), 'error');
                this.finishLocalTeyaCancel({ failed: true });
            },
        );
    }

    getPaymentStatus(saleId: number, paymentRequestId: string) {
        return this.teyaService.getPaymentStatus(saleId, paymentRequestId);
    }

    private pollPaymentStatus(saleId: number, paymentRequestId: string): void {
        let reachedFinalStatus = false;

        timer(0, POLL_INTERVAL_MS)
            .pipe(
                switchMap(() => this.getPaymentStatus(saleId, paymentRequestId)),
                takeWhile((status) => !this.isFinalPaymentStatus(status), true),
                takeUntil(this.stopPolling$),
                takeUntil(timer(MAX_POLL_MS)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (status: ITeyaPaymentStatus) => {
                    if (this.isFinalPaymentStatus(status)) {
                        reachedFinalStatus = true;
                        if (!this.isSuccessfulPaymentStatus(status)) {
                            this.toastNotificationsService.showToast(
                                this.translateService.instant('SHARED.SIMPLE_PAYMENT_MODAL.paymentFailed'),
                                'error',
                            );
                            this.teyaPaymentFailed.set(true);
                            this.waitingForTeyaTerminal.set(false);
                        } else {
                            this.refreshSaleData();
                            this.teyaPaymentFailed.set(false);
                        }
                    }
                },
                error: (error) => {
                    this.waitingForTeyaTerminal.set(false);
                    this.teyaPaymentFailed.set(true);
                    this.toastNotificationsService.showToast(error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'), 'error');
                },
                complete: () => {
                    if (!reachedFinalStatus) {
                        this.waitingForTeyaTerminal.set(false);
                        this.toastNotificationsService.showToast(
                            this.translateService.instant('SHARED.SIMPLE_PAYMENT_MODAL.paymentTimeout'),
                            'error',
                        );
                    }
                },
            });
    }

    refreshSaleData() {
        this.invoicingService.getSaleDetails(this.completeInvoiceData()?.id, this.currentStore()?.id).subscribe({
            next: (saleDetails) => {
                // Handle the updated sale details here
                this.invoicingService.existingSalePreview$.next(saleDetails);
                const currentCheckoutMode = this.checkoutMode();
                this.invoicingService.checkoutMode$.next(currentCheckoutMode);
                this.waitingForTeyaTerminal.set(false);
            },
            error: (error) => {
                this.toastNotificationsService.showToast(error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'), 'error');
                this.waitingForTeyaTerminal.set(false);
            },
        });
    }

    private isFinalPaymentStatus(status: ITeyaPaymentStatus): boolean {
        return FINAL_PAYMENT_STATUSES.includes(status.status);
    }

    private isSuccessfulPaymentStatus(status: ITeyaPaymentStatus): boolean {
        return status.status === SUCCESSFUL_PAYMENT_STATUS;
    }

    private isAlreadyFinishedCancelError(error: any): boolean {
        if (error?.errData?.code === 'TEYA_PAYMENT_ALREADY_FINISHED') {
            return true;
        }

        const message = String(error?.errMsg || error?.message || '').toLowerCase();

        return message.includes('already finished');
    }

    cancelTeyaTerminal() {
        const saleId = this.completeInvoiceData()?.id;
        const paymentRequestId = this.activeTeyaPaymentRequestId;

        if (!saleId || !paymentRequestId || this.cancellingTeyaPayment()) {
            this.finishLocalTeyaCancel({ failed: true });
            return;
        }

        this.cancellingTeyaPayment.set(true);
        this.teyaService.cancelPayment(saleId, paymentRequestId).subscribe({
            next: (status) => {
                this.cancellingTeyaPayment.set(false);
                this.handleCancelOutcome(saleId, paymentRequestId, status);
            },
            error: (error) => {
                this.cancellingTeyaPayment.set(false);

                if (this.isAlreadyFinishedCancelError(error)) {
                    this.reconcileAfterCancelConflict(saleId, paymentRequestId);
                    return;
                }

                this.finishLocalTeyaCancel({ failed: true });
                this.toastNotificationsService.showToast(
                    error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'),
                    'error',
                );
            },
        });
    }

    private handleCancelOutcome(saleId: number, paymentRequestId: string, status: ITeyaPaymentStatus) {
        if (this.isSuccessfulPaymentStatus(status)) {
            this.finishLocalTeyaWait();
            this.teyaPaymentFailed.set(false);
            this.refreshSaleData();
            return;
        }

        this.finishLocalTeyaCancel({ failed: true });
    }

    private reconcileAfterCancelConflict(saleId: number, paymentRequestId: string) {
        this.teyaService.getPaymentStatus(saleId, paymentRequestId, true).subscribe({
            next: (status) => {
                this.handleCancelOutcome(saleId, paymentRequestId, status);
            },
            error: (error) => {
                this.finishLocalTeyaCancel({ failed: true });
                this.toastNotificationsService.showToast(
                    error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'),
                    'error',
                );
            },
        });
    }

    private finishLocalTeyaWait() {
        this.callTeyaTerminal.set(false);
        this.stopPolling$.next();
        this.activeTeyaPaymentRequestId = null;
    }

    private finishLocalTeyaCancel({ failed = true }: { failed?: boolean } = {}) {
        this.callTeyaTerminal.set(false);
        this.stopPolling$.next();
        this.waitingForTeyaTerminal.set(false);
        this.teyaPaymentFailed.set(failed);
        this.activeTeyaPaymentRequestId = null;
    }

    goToStart() {
        this.invoicingService.clearAll();
        this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/calendar');
    }

    goToSales() {
        this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/sales');
    }

    downloadInvoice(data: any) {
        this.api.downloadUrlAsFile(data.invoicePdfUrl, `buk_${data.invoiceReference}.pdf`);
    }

    printInvoice(data: any) {
        this.api.getBlobFromUrl(data.invoicePdfUrl).subscribe((blob) => {
            this.toolsService.printBlob(blob);
        });
    }

    printTicket(data: any) {
        if (!data?.invoiceTicketPdfUrl) {
            return;
        }

        this.api.getBlobFromUrl(data.invoiceTicketPdfUrl).subscribe((blob) => {
            this.toolsService.printBlob(blob);
        });
    }

    emailInvoice(sale: any) {
        this.invoicingService.requestEmailInvoice({
            merchantClientEmail: sale.merchantClient?.email,
            saleId: sale.id,
            saleInvoiceId: sale?.saleInvoices?.[0]?.id,
        });
    }

    openSaleDetails() {
        this.bsModalService.show(SaleDetailsComponent, {
            initialState: { sale: this.completeInvoiceData() },
            class: 'modal-lg',
        });
    }
}
